<template>
    <button class="nav-tab code-button install-button" :disabled="loading" @click="installSignature">
        <span v-if="loading" class="loader"></span>
        {{ $t('mainPanel.install') }}
    </button>
</template>

<script>
import axios from 'axios';
import { BACKEND_URL } from '@/environments';

export default {
    name: 'InstallButton',
    props: {
        originalFormData: { type: Object, required: true }
    },
    data() {
        return {
            loading: false,
            templateId: null
        };
    },
    computed: {
        hasAuthToken() {
            return !!localStorage.getItem('generatorAuthToken');
        },
        decodedToken() {
            try {
                const token = localStorage.getItem('generatorAuthToken');
                return token ? JSON.parse(atob(token.split('.')[1])) : null;
            } catch {
                return null;
            }
        },
        adminEmail() {
            return this.decodedToken?.email || '';
        },
        domainName() {
            return this.decodedToken?.domain || '';
        }
    },
    methods: {
        getBaseFontSize() {
            switch (this.originalFormData.fontSize) {
                case 'small': return 12;
                case 'large': return 16;
                case 'medium':
                default: return 14;
            }
        },

        async installSignature() {
            if (!this.hasAuthToken) return;
            this.loading = true;

            try {
                if (typeof this.$parent.updateFormDataForExport === 'function') {
                    this.$parent.updateFormDataForExport();
                    await this.$parent.$nextTick();
                }

                const sigComp = this.$parent.$refs.signaturePreview;
                if (!sigComp) throw new Error('Signature preview not found');
                const rawHtml = sigComp.$el.innerHTML;
                const processed = typeof this.$parent.processHtml === 'function'
                    ? this.$parent.processHtml(rawHtml)
                    : rawHtml;
                const signatureCode = String(processed || '')
                    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
                    .replace(/<\/?(?:iframe|object|embed|link|meta|base|form)\b[^>]*>/gi, '')
                    .replace(/\s+on[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
                    .replace(/\s(?:href|src|xlink:href)\s*=\s*(["'])\s*javascript:[^"']*\1/gi, '');

                if (typeof this.$parent.restoreOriginalFormData === 'function') {
                    this.$parent.restoreOriginalFormData();
                    await this.$parent.$nextTick();
                }

                const authToken = localStorage.getItem('generatorAuthToken');
                const authHeaders = authToken
                    ? { Authorization: `Bearer ${authToken}` }
                    : {};

                const { data: gen } = await axios.get(
                    `${BACKEND_URL}/generator/check-link-name`,
                    { params: { email: this.adminEmail } }
                );
                const name = gen.candidate;

                const domResp = await axios.post(
                    `${BACKEND_URL}/generator/check-or-create-domain`,
                    { domain: this.domainName },
                    { headers: authHeaders }
                );
                if (!domResp.data.success) {
                    throw new Error(domResp.data.error || 'Cannot create domain.');
                }

                const { data: saveGen } = await axios.post(
                    `${BACKEND_URL}/generator/save`,
                    {
                        settings: this.originalFormData,
                        email: this.adminEmail,
                        linkName: name,
                        originalLinkName: name
                    }
                );

                const p = this.originalFormData;

                const bannerAddon = p.banner?.url
                    ? {
                        imageUrl: p.banner.url,
                        imageWidth: p.bannerWidth,
                        name: 'Generator Banner'
                    }
                    : null;

                const safeDisclaimer = String(p.disclaimer || '')
                    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
                    .replace(/<\/?(?:iframe|object|embed|link|meta|base|form)\b[^>]*>/gi, '')
                    .replace(/\s+on[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
                    .replace(/\s(?:href|src|xlink:href)\s*=\s*(["'])\s*javascript:[^"']*\1/gi, '');
                const disclaimerAddon = safeDisclaimer
                    ? {
                        name: 'Generator Disclaimer',
                        htmlContent: `
                            <table cellspacing="0" cellpadding="0" border="0" width="${p.disclaimerWidth ? '100%' : p.signatureWidth}">
                            <tr>
                                <td style="padding-top: 3px">
                                <p style="
                                    font-family: ${p.fontFamily};
                                    color: ${p.addonsColor};
                                    font-size: ${this.getBaseFontSize() - 2}px;
                                ">
                                    ${safeDisclaimer}
                                </p>
                                </td>
                            </tr>
                            </table>`
                    }
                    : null;

                const payload = {
                    domain: this.domainName,
                    name,
                    signatureCode,
                    phoneLabel: p.phoneIcon?.trim()
                        ? `<td style="padding:3px;border-radius:${p.contactIconShape || ''}%;background-color:${p.contactIconColor || ''}"><img width="16" height="16" alt="phone" src="${p.phoneIcon}" style="display:block;"></td>`
                        : p.phoneLabel || '',
                    mobileLabel: p.mobileIcon?.trim()
                        ? `<td style="padding:3px;border-radius:${p.contactIconShape || ''}%;background-color:${p.contactIconColor || ''}"><img width="16" height="16" alt="mobile" src="${p.mobileIcon}" style="display:block;"></td>`
                        : p.mobileLabel || '',
                    emailLabel: p.emailIcon?.trim()
                        ? `<td style="padding:3px;border-radius:${p.contactIconShape || ''}%;background-color:${p.contactIconColor || ''}"><img width="16" height="16" alt="email" src="${p.emailIcon}" style="display:block;"></td>`
                        : p.emailLabel || '',
                    banner: bannerAddon,
                    disclaimer: disclaimerAddon,
                    generatorLink: saveGen.id
                };

                const { data: saveResp } = await axios.post(
                    `${BACKEND_URL}/generator/save-signature`,
                    payload,
                    { headers: authHeaders }
                );
                if (!saveResp.templateId) {
                    throw new Error(saveResp.error || 'Error saving signature.');
                }
                this.templateId = saveResp.templateId;

                window.location.href = `https://app.gsignature.com/signatures/custom/${this.templateId}`;
            } catch (e) {
                console.error('Install failed:', e);
                this.$emit('error', e);
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
.install-button[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
}

.loader {
    box-sizing: border-box;
    flex: 0 0 auto;
    display: block;
    width: 16px;
    height: 16px;
    min-width: 16px;
    min-height: 16px;
    border: 2px solid #f3f3f3;
    border-top-color: #555;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>